/**
 * Independent components
 * These components do not include any other components
 *
 * This order mitigates problems that might arise when trying to import components
 * that have circular dependencies to other components.
 * Note: import-order also affects to the generated CSS bundle file.
 *
 * Read more:
 * https://medium.com/visual-development/how-to-fix-nasty-circular-dependency-issues-once-and-for-all-in-javascript-typescript-a04c987cf0de
 */
import loadable from '@loadable/component';

// Icons
export { default as IconAdd } from './IconAdd/IconAdd';
export { default as IconArchery } from './IconArchery/IconArchery';
export { default as IconArrowHead } from './IconArrowHead/IconArrowHead';
export { default as IconBannedUser } from './IconBannedUser/IconBannedUser';
export { default as IconCheckmark } from './IconCheckmark/IconCheckmark';
export { default as IconChevron } from './IconChevron/IconChevron';
export { default as IconClose } from './IconClose/IconClose';
export { default as IconCloseChevron } from './IconCloseChevron/IconCloseChevron';
export { default as IconDelete } from './IconDelete/IconDelete';
export { default as IconDisputeOrder } from './IconDisputeOrder/IconDisputeOrder';
export { default as IconEdit } from './IconEdit/IconEdit';
export { default as IconEmailAttention } from './IconEmailAttention/IconEmailAttention';
export { default as IconEmailSent } from './IconEmailSent/IconEmailSent';
export { default as IconEmailSuccess } from './IconEmailSuccess/IconEmailSuccess';
export { default as IconFishing } from './IconFishing/IconFishing';
export { default as IconHunting } from './IconHunting/IconHunting';
export { default as IconInquiry } from './IconInquiry/IconInquiry';
export { default as IconIntercom } from './IconIntercom/IconIntercom';
export { default as IconKeys } from './IconKeys/IconKeys';
export { default as IconKeysSuccess } from './IconKeysSuccess/IconKeysSuccess';
export { default as IconReviewStar } from './IconReviewStar/IconReviewStar';
export { default as IconReviewUser } from './IconReviewUser/IconReviewUser';
export { default as IconSearch } from './IconSearch/IconSearch';
export { default as IconSettings } from './IconSettings/IconSettings';
export { default as IconSpinner } from './IconSpinner/IconSpinner';
export { default as IconSuccess } from './IconSuccess/IconSuccess';
export { default as IconManufacturer } from './IconManufacturer/IconManufacturer';
export { default as IconLocation } from './IconsSeller/IconLocation';
export { default as IconPhone } from './IconsSeller/IconPhone';
export { default as IconWeb } from './IconsSeller/IconWeb';

// Typography
export { H1, H2, H3, H4, H5, H6, Heading } from './Heading/Heading';

// Other independent components
export { default as AspectRatioWrapper } from './AspectRatioWrapper/AspectRatioWrapper';
export { default as CartIconBadge } from './CartIconBadge/CartIconBadge';
export { default as CloseListingModal } from './CloseListingModal/CloseListingModal';
export { default as ExpandingTextarea } from './ExpandingTextarea/ExpandingTextarea';
export { default as ExternalLink } from './ExternalLink/ExternalLink';
export { default as HistoricalPriceChart } from './HistoricalPriceChart/HistoricalPriceChart';
export { default as Form } from './Form/Form';
export { default as LimitedAccessBanner } from './LimitedAccessBanner/LimitedAccessBanner';
// export { default as OptimizedImage } from './ListingCard/OptimizedImage';
export { default as BrandCarousel } from './BrandCarousel/BrandCarousel';
export { default as Logo } from './Logo/Logo';
export { default as NamedLink } from './NamedLink/NamedLink';
export { default as NamedRedirect } from './NamedRedirect/NamedRedirect';
export { default as NotificationBadge } from './NotificationBadge/NotificationBadge';
export { default as OutsideClickHandler } from './OutsideClickHandler/OutsideClickHandler';
export { default as Promised } from './Promised/Promised';
export { default as PropertyGroup } from './PropertyGroup/PropertyGroup';
export { default as RangeSlider } from './RangeSlider/RangeSlider';
export { default as ResponsiveBackgroundImageContainer } from './ResponsiveBackgroundImageContainer/ResponsiveBackgroundImageContainer';
export { default as ResponsiveImage } from './ResponsiveImage/ResponsiveImage';
export { default as OptimizedImage } from './ListingCard/OptimizedImage';
export { default as StoreCard } from './ListingCard/StoreCard';
export { default as TimeRange } from './TimeRange/TimeRange';
export { default as TopbarIframe } from './TopbarIframe/TopbarIframe';
export { default as UserDisplayName } from './UserDisplayName/UserDisplayName';
export { default as ValidationError } from './ValidationError/ValidationError';

/**
 * Composite components
 * These components include other components
 */

//////////////////////////////////////////////////////////
// First components that include only atomic components //
//////////////////////////////////////////////////////////

export { default as Button, InlineTextButton, PrimaryButton, PrimaryButtonInline, SecondaryButton, SecondaryButtonInline, SocialLoginButton } from './Button/Button';
export { default as ImageFromFile } from './ImageFromFile/ImageFromFile';
export { default as ListingLink } from './ListingLink/ListingLink';
export { default as LinkedLogo } from './Logo/LinkedLogo';
export { default as PaginationLinks } from './PaginationLinks/PaginationLinks';
export { default as ReviewRating } from './ReviewRating/ReviewRating';

// Menu
export { default as Menu } from './Menu/Menu';
export { default as MenuContent } from './MenuContent/MenuContent';
export { default as MenuItem } from './MenuItem/MenuItem';
export { default as MenuLabel } from './MenuLabel/MenuLabel';

// Modal
export { default as Modal } from './Modal/Modal';
export { default as ModalInMobile } from './ModalInMobile/ModalInMobile';

// Fields (for Final Form)
export { default as FieldCheckbox } from './FieldCheckbox/FieldCheckbox';
export { default as FieldCurrencyInput } from './FieldCurrencyInput/FieldCurrencyInput';
export { default as FieldRadioButton } from './FieldRadioButton/FieldRadioButton';
export { default as FieldReactSelect } from './FieldReactSelect/FieldReactSelect';
export { default as FieldReviewRating } from './FieldReviewRating/FieldReviewRating';
export { default as FieldSelect } from './FieldSelect/FieldSelect';
export { default as FieldSelectTree } from './FieldSelectTree/FieldSelectTree';
export { default as FieldTextInput } from './FieldTextInput/FieldTextInput';
export { default as SubscriptionForm } from './SubscriptionForm/SubscriptionForm';

// Fields that use other Fields
export { default as FieldBoolean } from './FieldBoolean/FieldBoolean';
export { default as FieldCheckboxGroup } from './FieldCheckboxGroup/FieldCheckboxGroup';
export { default as FieldPhoneNumberInput } from './FieldPhoneNumberInput/FieldPhoneNumberInput';
export { FieldLocationAutocompleteInput, default as LocationAutocompleteInput } from './LocationAutocompleteInput/LocationAutocompleteInput';
// Fields and inputs using old naming pattern
export { default as StripeBankAccountTokenInputField } from './StripeBankAccountTokenInputField/StripeBankAccountTokenInputField';
// Fields wrapping react-dates
// NOTE: these are code-splitted since the library is heavy and needed only on couple of pages
export const FieldDateInput = loadable(() => import(/* webpackChunkName: "FieldDateInput" */ './FieldDateInput/FieldDateInput'));
export const FieldDateRangeController = loadable(() => import(/* webpackChunkName: "FieldDateRangeController" */ './FieldDateRangeController/FieldDateRangeController'));
export const FieldDateRangeInput = loadable(() => import(/* webpackChunkName: "FieldDateRangeInput" */ './FieldDateRangeInput/FieldDateRangeInput'));

// Tab navigation
export { default as TabNav } from './TabNav/TabNav';
export { ButtonTabNavHorizontal, LinkTabNavHorizontal } from './TabNavHorizontal/TabNavHorizontal';
export { default as Tabs } from './Tabs/Tabs';
export { default as UserNav } from './UserNav/UserNav';
export { default as ItemSourceToggle } from './ItemSourceToggle/ItemSourceToggle';

///////////////////////////////////////////////
// These components include other components //
///////////////////////////////////////////////

export { default as Avatar, AvatarLarge, AvatarMedium, AvatarSmall } from './Avatar/Avatar';
export { default as CustomExtendedDataField } from './CustomExtendedDataField/CustomExtendedDataField';
export { default as CategoryListingCard } from './ListingCard/CategoryListingCard';
export { default as CompactListingCard } from './ListingCard/CompactListingCard';
export { default as DetailListingCard } from './ListingCard/DetailListingCard';
export { default as ListingCard } from './ListingCard/ListingCard';
export { default as ListListingCard } from './ListingCard/ListListingCard';
export { default as SearchListingCard } from './ListingCard/SearchListingCard';
export { default as SectionListingCard } from './ListingCard/SectionListingCard';
export { default as Map } from './Map/Map';
export { default as OrderBreakdown } from './OrderBreakdown/OrderBreakdown';
export { default as OrderPanel } from './OrderPanel/OrderPanel';
export { default as Page } from './Page/Page';
export { default as DealerPricingBreakdown } from './DealerPricingBreakdown/DealerPricingBreakdown';
export { default as DealerSelectionField } from './DealerSelectionField/DealerSelectionField';
export { default as Reviews } from './Reviews/Reviews';
export { default as SavedCardDetails } from './SavedCardDetails/SavedCardDetails';
export { default as StripeConnectAccountStatusBox } from './StripeConnectAccountStatusBox/StripeConnectAccountStatusBox';
export { default as StripePaymentAddress } from './StripePaymentAddress/StripePaymentAddress';

// Forms
export { default as StripeConnectAccountForm } from './StripeConnectAccountForm/StripeConnectAccountForm';

//////////////////////////////////////////////
// Page sections and modal content wrappers //
//////////////////////////////////////////////

export { default as LayoutComposer, LayoutSideNavigation, LayoutSingleColumn } from './LayoutComposer';
export { default as LoginPromptModal } from './LoginPromptModal/LoginPromptModal';
export { default as MaintenanceMode } from './MaintenanceMode/MaintenanceMode';
export { default as ModalMissingInformation } from './ModalMissingInformation/ModalMissingInformation';
export { default as ModalPendingGroups } from './ModalPendingGroups/ModalPendingGroups';
export { default as SellerInquiryModal } from './SellerInquiryModal/SellerInquiryModal';
